import React, { useRef, useState } from 'react';
import { countryCodeFor, countryName } from '@repo/front-end-utils';

import AddressFields, { type AddressLine } from './AddressFields';
import SummaryCard, { SummaryRows } from './SummaryCard';
import type { TwinDetailsForm } from './twinDetailsForm';

/**
 * The summary's **Address** card (`twin-details-page-summary`, R4; frame 03, frame 04 without the
 * parked Keep / Move ask). Reads first; in edit it is `AddressFields`, the same component and the
 * same save points as the create route (country on choose, lines on blur).
 *
 * Correcting the address moves nothing else (epic outcome 2): the step sends only the field changed,
 * and off the create route the timezone is kept (`zoneWrite`); where the address now points to
 * another zone, the Timezone card says so.
 */

const NOT_SET = 'Not set';

const joined = (parts: string[], separator: string): string =>
  parts
    .map(part => part.trim())
    .filter(Boolean)
    .join(separator) || NOT_SET;

/** R4.1: the country's English name, or what is stored when it is not a listed code. */
const countryRow = (stored: string): string => {
  const kept = stored.trim();
  if (!kept) return NOT_SET;
  const code = countryCodeFor(kept);
  return (code && countryName(code)) || kept;
};

interface Props {
  /** What the twin holds. */
  saved: TwinDetailsForm;
  /** What is on screen in edit. */
  form: TwinDetailsForm;
  onCountry: (code: string | null) => void;
  onLineChange: (field: AddressLine, value: string) => void;
  onLineBlur: (field: AddressLine) => void;
  onLineRestore: (field: AddressLine) => void;
}

const SummaryAddressCard: React.FC<Props> = ({
  saved,
  form,
  onCountry,
  onLineChange,
  onLineBlur,
  onLineRestore,
}) => {
  const [editing, setEditing] = useState(false);
  const fieldsRef = useRef<HTMLDivElement>(null);

  return (
    <SummaryCard
      surface="on-scene"
      title="Address"
      glyph="location-dot"
      discTone="aqua"
      editing={editing}
      onEdit={() => setEditing(true)}
      onDone={() => {
        // *Done* never holds an edit back (R2.4): a line still focused is left first, which saves it.
        const active = document.activeElement;
        if (active instanceof HTMLElement && fieldsRef.current?.contains(active)) active.blur();
        setEditing(false);
      }}
    >
      {editing ? (
        <div ref={fieldsRef}>
          <AddressFields
            storedCountry={saved.country}
            form={form}
            onCountry={onCountry}
            onLineChange={onLineChange}
            onLineBlur={onLineBlur}
            onLineRestore={onLineRestore}
          />
        </div>
      ) : (
        <SummaryRows
          rows={[
            {
              key: 'address',
              label: 'Address',
              value: joined([saved.address_1, saved.address_2], ', '),
            },
            { key: 'town', label: 'Town', value: joined([saved.city, saved.postcode], ' ') },
            { key: 'country', label: 'Country', value: countryRow(saved.country) },
          ]}
        />
      )}
    </SummaryCard>
  );
};

export default SummaryAddressCard;
